/**
 * API 客户端
 *
 * 后端统一响应格式：{ success, data } / { success:false, code, message, details }
 * 这里把错误统一抛成 ApiError，业务层只看 message（后端已给中文文案）。
 */

export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  traceId?: string;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** 是不是"需要充值"类错误 */
  get isInsufficientBalance(): boolean {
    return this.code === 'E-BILL-001' || this.code === 'E-BILL-005';
  }
  /** 是不是"未登录" */
  get isUnauthorized(): boolean {
    return this.code === 'E-AUTH-001' || this.code === 'E-AUTH-002';
  }
}

const TOKEN_KEY = 'gfc_token';

/**
 * BUG-11：脚本里的事实分级条目。
 *
 * `level` 决定界面怎么显示，只有三档，语义不能混：
 *   · `verified`      系统核过的（当前没有外部事实源，实际不会出现）
 *   · `user_provided` 用户自己在生意档案里写的 —— 我们没编，但也没核
 *   · `unverified`    模型自己写出来、没有任何依据的 —— **必须标出来让用户核**
 *
 * 用户最该看到的是第三档。把三档混在一起显示等于没分级。
 */
export interface ClaimItem {
  category: string;
  label: string;
  excerpt: string;
  level: 'verified' | 'user_provided' | 'unverified';
  hint: string;
}

export interface ClaimSummary {
  total: number;
  unverified: number;
  userProvided: number;
  /** 含「包就业」类承诺性表述（广告法禁止），必须删掉 */
  hasBlockingClaim: boolean;
  blockReason?: string;
}

/**
 * BUG-11（选题质量）：选题的评估快照。
 *
 * 报告原文：「推荐度说明是 AI 评估而非实测预测；显示维度与依据，
 *            不断言完播和私信一定高。」
 *
 * ⚠️ `overall` / `scoreBreakdown[*]` 为 `null` 的含义是**拿不到**，不是 0：
 *    · 模型只给了单一分数时，五个维度全是 null（我们不会替它拆）
 *    · 老选题（评分改造之前生成的）整个 `assessment` 是 null
 * 界面遇到 null 必须显示「—」并说明原因，绝不能显示 0 分。
 */
export interface TopicDisputeHit {
  category: 'smear_peer' | 'engineer_conflict' | 'unsupported_prediction';
  label: string;
  excerpt: string;
  hint: string;
}

export interface TopicHypothesis {
  metric: string;
  expectation: string;
  howToVerify: string;
}

export interface TopicAssessment {
  /** 恒为 true。界面上任何显示分数的地方都要带「AI 估计」字样 */
  isAiEstimate: true;
  /** breakdown=五维齐全 / model_single=模型只给了单一分 / none=没有分数 */
  scoreSource: 'breakdown' | 'model_single' | 'none';
  /** 维度中文标签与说明由后端下发（单一来源，避免前后端两份常量漂移） */
  dimensionLabels: Record<string, string>;
  dimensionHints: Record<string, string>;
  modelReportedScore: number | null;
  scoreBreakdown: {
    goalFit: number | null;
    evidence: number | null;
    conversion: number | null;
    cost: number | null;
    risk: number | null;
  };
  overall: number | null;
  evidence: string[];
  unverifiedClaims: ClaimItem[];
  claimSummary: ClaimSummary;
  hypotheses: TopicHypothesis[];
  audience: string | null;
  userConcern: string | null;
  ctaPath: string | null;
  handoff: string | null;
  dispute: { level: 'none' | 'low' | 'medium' | 'high'; hits: TopicDisputeHit[] };
  confidenceNote: string;
}

/** 列表接口给的现成结论，避免前端各写一套判断 */
export interface TopicQuality {
  isAiEstimate: true;
  overall: number | null;
  disputeLevel: 'none' | 'low' | 'medium' | 'high';
  disputeLabels: string[];
  pendingClaimCount: number;
  hypothesisCount: number;
  hasBreakdown: boolean;
}

/** 选题库列表里的一行 */
export interface TopicItem {
  id: string;
  title: string;
  /** BUG-11: null = 模型没给分，界面必须显示「—」而不是 0 */
  score: number | null;
  /** 模型自由文本，仅作参考，不做 i18n 映射 */
  type: string | null;
  reason: string | null;
  /** 受约束枚举（CONTENT_FORM），界面标签必须按它来 */
  contentForm: string | null;
  suggestedDurationSec: number | null;
  suggestedShots: number | null;
  needsCharacterConsistency: boolean;
  status: string;
  assessment: TopicAssessment | null;
  /** null = 这条早于评分改造，没有当时的评估结论（**不是"没问题"**） */
  quality: TopicQuality | null;
  /**
   * ★ BUG-B：这条选题**已经有脚本了**（决定点「写脚本」要不要先弹二次确认）。
   * 来自 scripts.topic_id，是库里的真相，不是前端猜的 status。
   */
  hasScript: boolean;
  createdAt: string;
}

/**
 * 选题库列表的一页（BUG-A：2026-10-04 起接口返回分页对象，不再是裸数组）
 *
 * ⚠️ `total` 是"去重后、在服务端扫描窗口内"的条数。`truncated=true` 时它是**下限**
 *    —— 界面必须照实说，不能拿它当精确总数。
 */
export interface TopicPage {
  items: TopicItem[];
  total: number;
  skip: number;
  take: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  sort: 'rank' | 'latest';
  q: string | null;
  truncated: boolean;
}

/** 脚本里的一个镜头（分镜生成后多一个 imagePrompt） */
export interface ScriptShot {
  no: number;
  from: number;
  to: number;
  visual: string;
  voice: string;
  /** null/缺省 = 这一步还没生成分镜画面提示词 */
  imagePrompt?: string | null;
}

/** 脚本（生成接口与"读回已有脚本"接口返回同一种形状） */
export interface ScriptDto {
  publicId: string;
  id: string;
  title: string;
  hook: string | null;
  durationSec: number | null;
  shots: ScriptShot[];
  cta: string | null;
  compliance: {
    passed: boolean;
    hitCount: number;
    hits: Array<{
      term: string;
      context: string;
      category: string;
      action: string;
      message: string;
      suggestion?: string;
    }>;
    note: string;
  };
  /** BUG-11: 事实分级结果。level=unverified 的必须用户自己核过才能发布 */
  unverifiedClaims: ClaimItem[];
  claimSummary: ClaimSummary | null;
  /** 本次请求实际扣掉的积分。**读回历史脚本时恒为 0**（见 fromHistory） */
  pointsCharged: number;
  /** true = 从库里读回来的（没扣费）；缺省/false = 本次刚生成 */
  fromHistory?: boolean;
  status?: string;
  createdAt?: string;
  /** 库里存的脚本标题与选题标题一致，这里给出选题当前状态供界面判断 */
  topicStatus?: string;
}

/** 分镜（生成接口与"读回已有分镜"接口返回同一种形状） */
export interface StoryboardDto {
  scriptId: string;
  /**
   * 全片统一风格。**读回历史分镜时恒为 null** ——
   * 生成时风格已经逐条拼进 imagePrompt，库里没有单独存这一项，我们不反推一个出来。
   */
  globalStyle: string | null;
  shotCount: number;
  filled: number;
  shots: Array<{ no: number; from: number; to: number; visual: string; voice: string; imagePrompt: string | null }>;
  pointsCharged: number;
  modelId: string | null;
  /** true = 从库里读回来的（没扣费） */
  fromHistory?: boolean;
  meta?: { costYuan: string };
}

/**
 * ★ 一键分发（半自动）：这条作品在某个平台**能不能直接发**。
 *
 * 这是纯规则算出来的，不调模型也不用平台接口 —— 但它是商家最需要的一行结论：
 * 小红书要 3:4 且上限 60 秒、B 站是 16:9 且至少 60 秒，
 * 把竖屏 15 秒片直接传上去只会被拒。
 */
export interface PlatformFit {
  platform: string;
  label: string;
  /** 时长与画幅都合适 */
  ready: boolean;
  /** 需要先导出转码（画幅不一致） */
  needRefit: boolean;
  durationIssue: string | null;
  spec: { ratio: string; width: number; height: number; minSec: number; maxSec: number };
  note: string;
}

/** 某个平台的可粘贴发布包 */
export interface DistributionPack {
  title: string;
  description: string;
  hashtags: string[];
  coverText: string | null;
  /** 该平台的 AI 标识声明话术（各平台入口不一样，必须逐平台给） */
  aiLabelNote: string;
  publishTip: string;
}

export interface DistributionDto {
  id: string;
  platform: string;
  platformLabel: string;
  pack: DistributionPack | null;
  adapt: PlatformFit | null;
  /**
   * pending=待发布 / published=用户确认已发 / failed / skipped
   * ★ 只有用户点确认才会是 published。系统不会替他声明已发布。
   */
  status: 'pending' | 'published' | 'failed' | 'skipped';
  /**
   * manual = 要用户自己在 App 里点发布。
   * ★ 界面必须把这个说清楚，否则用户以为我们替他发了，然后干等。
   */
  mode: 'manual' | 'auto';
  accountId: string | null;
  publishedAt: string | null;
  publishedUrl: string | null;
  note: string | null;
  aiLabelNote: string | null;
  createdAt: string;
}

/**
 * 作品日指标（M8 数据复盘）
 *
 * ⚠️ 计数字段在服务端是 BigInt，接口**一律以字符串回**（BigInt 不能直接 JSON 化）。
 *    展示时按数字用；做算术前先 `Number(...)`。
 */
export interface WorkMetricDto {
  id: string;
  workId: string;
  platform: string;
  platformLabel: string;
  /** 业务日键 `YYYY-MM-DD`（北京时间那一天）—— 直接显示，不要再自己换算时区 */
  metricDate: string;
  playCount: string;
  likeCount: string;
  commentCount: string;
  shareCount: string;
  collectCount: string;
  /** 粉丝增量，可负（掉粉） */
  followerDelta: number;
  conversionCount: number;
  conversionNote: string | null;
  /** MANUAL = 用户自己登记的（不是平台回流） */
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformAccountDto {
  id: string;
  platform: string;
  platformLabel: string;  nickname: string;
  profileUrl: string | null;
  /** 用户自述，我们核不了 —— 字段名如实反映这一点 */
  verifiedSelfReported: boolean;
  boundVia: 'manual' | 'oauth';
  isDefault: boolean;
  createdAt: string;
}

// ============================================================
//  账号经营（P1）
//
//  ★ 四个「账号」概念不许混叫（P0-工程契约 §1）：
//    企业空间 = Tenant｜经营账号 = BusinessAccount
//    平台账号 = PlatformAccount（抖音等授权身份）｜角色资产 = Asset
// ============================================================

/** 经营账号状态 */
export type AccountStatus = 'active' | 'paused' | 'archived';
/** 策略版本状态：draft → approved → superseded（批准后不可改，只能出新版） */
export type StrategyStatus = 'draft' | 'approved' | 'superseded';
export type ColumnStatus = 'active' | 'paused';
export type WeeklyPlanStatus = 'draft' | 'confirmed' | 'archived';
export type PlanItemStatus = 'planned' | 'producing' | 'published' | 'canceled';

export interface OperationAccountDto {
  id: string;
  name: string;
  audience: string | null;
  goal: string | null;
  status: AccountStatus;
  columnCount: number;
  /** 当前已批准策略的版本号；没有批准过就是 null（界面要出"先定策略"的引导，不是显示 0） */
  currentStrategyVersion: number | null;
  updatedAt: string;
}

export interface OperationAccountDetail extends OperationAccountDto {
  note: string | null;
  strategy: OperationStrategyDto | null;
  columns: OperationColumnDto[];
  recentPlans: OperationWeeklyPlanDto[];
}

export interface OperationStrategyDto {
  id: string;
  accountId: string;
  version: number;
  status: StrategyStatus;
  positioning: unknown;
  pillars: unknown;
  cadence: unknown;
  /** 周预算上限 —— 积分 */
  weeklyBudgetPoints: number | null;
  /** 周预算上限 —— 微元。与积分**分开存**，显示时不要合成一个数字 */
  weeklyBudgetMicro: string | null;
  note: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OperationColumnDto {
  id: string;
  accountId: string;
  name: string;
  purpose: string | null;
  weight: number | null;
  sortOrder: number;
  status: ColumnStatus;
}

export interface OperationWeeklyPlanDto {
  id: string;
  accountId: string;
  /** 周一（Asia/Shanghai），YYYY-MM-DD */
  weekStartDate: string;
  /** 周日（含） */
  weekEndDate: string;
  status: WeeklyPlanStatus;
  budgetPoints: number | null;
  budgetMicro: string | null;
  itemCount: number;
  confirmedAt: string | null;
  createdAt: string;
}

export interface OperationPlanItemDto {
  id: string;
  planId: string;
  columnId: string | null;
  columnName: string | null;
  title: string;
  format: string | null;
  plannedDate: string | null;
  status: PlanItemStatus;
  estimatedPoints: number | null;
  /** 微元；**null = 未知**（不是 0） */
  estimatedMicro: string | null;
  movedFromDate: string | null;
  movedReason: string | null;
  sortOrder: number;
}

export interface OperationWeeklyPlanDetail extends OperationWeeklyPlanDto {
  items: OperationPlanItemDto[];
}

/**
 * 本周预算口径（A3）。
 *
 * ★ `usedPoints` 是 `null` + `usedUnavailableReason`，**不是 0**：
 *   执行成本要等 P2 的内容任务/工作流接入后才能统计。
 *   "未采集"与"确实是 0"是两件事，界面上必须分开（P0 硬规则）。
 */
export interface OperationBudgetDto {
  weekStart: string;
  weekEnd: string;
  limitPoints: number | null;
  limitMicro: string | null;
  committedPoints: number;
  /** 预估为空的条目数 —— 它们**没有**被当成 0 计入 committedPoints */
  unknownCount: number;
  usedPoints: number | null;
  usedUnavailableReason: string | null;
}

/** 同步出片的结果（作品 DTO） */
export interface ProduceWork {  workId: string;
  title: string;
  videoUrl: string;
  coverUrl: string | null;
  durationSec: number;
  ratio: string;
  width: number;
  height: number;
  sizeBytes: number;
  shotCount: number;
  mode: string;
  aiLabel: { status: string; explicit: boolean; implicit: boolean; exportable: boolean; note: string };
  pointsCharged: number;
  /**
   * BUG-16：**不再声明 costYuan**。
   * 它是我们调用模型的真实成本，前端从来没显示过（死字段），
   * 但接口一直在返回 —— 用户开开发者工具就能读到。
   * 平台侧要这个数走 `GET /admin/tasks/:id/diagnostics`。
   */
}

/** 异步出片的受理回执 */
export interface ProduceQueued {
  queued: true;
  taskId: string;
  jobId: string | null;
  mode: string;
  costTier: string;
  pollUrl: string;
  note: string;
}

export type ProduceResult = ProduceWork | ProduceQueued;

/**
 * 判别出片返回的是"已排队"还是"成品"。
 *
 * 用 `queued === true` 而不是"有没有 workId"来判断：前者是后端**显式声明**的
 * 语义，后者是推断 —— 万一将来同步返回里 workId 为空，推断就会误判成异步。
 */
export function isQueuedProduce(r: ProduceResult): r is ProduceQueued {
  return (r as ProduceQueued).queued === true;
}


export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null): void {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** 幂等键：付费确认、重试、下单必须带，防连点/刷新重复扣费 */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (opts.idempotencyKey) headers['x-idempotency-key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method: opts.method ?? 'GET',
      headers,
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (e) {
    // 网络层失败（断网/服务没起）—— 与业务错误区分开
    throw new ApiError('E-NET', '网络连接不上，请检查网络后重试', 0);
  }

  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError('E-GEN-005', '服务返回格式异常', res.status);
  }

  const env = json as { success?: boolean; data?: T; code?: string; message?: string; details?: unknown };

  if (!res.ok || env?.success === false) {
    throw new ApiError(
      env?.code ?? 'E-GEN-005',
      env?.message ?? '操作失败，请稍后重试',
      res.status,
      env?.details,
    );
  }

  return env.data as T;
}

// ============================================================
//  业务接口
// ============================================================

export interface AuthUser {
  id: string;
  phone: string | null;
  nickname: string | null;
  role: string;
  realNameVerified: boolean;
}
export interface AuthTenant {
  id: string;
  publicId: string;
  name: string;
  type: string;
  isServiceClient: boolean;
}
export interface BalanceInfo {
  available: string;
  frozen: string;
  total: string;
  totalRecharged?: string;
  totalConsumed?: string;
  status?: string;
}

export const api = {
  // ---- 认证 ----
  register: (body: { phone: string; password: string; companyName: string; nickname?: string }) =>
    request<{ token: string; user: AuthUser; tenant: AuthTenant; balance: string }>('/auth/register', {
      method: 'POST',
      body,
    }),

  login: (body: { phone: string; password: string }) =>
    request<{ token: string; user: AuthUser; tenant: AuthTenant; balance: string }>('/auth/login', {
      method: 'POST',
      body,
    }),

  me: () => request<{ user: AuthUser; tenant: AuthTenant; balance: BalanceInfo }>('/auth/me'),

  /**
   * 修改密码（当前登录用户自己改）
   *
   * ★ 为什么是 POST `/auth/change-password` 而不是 PATCH `/auth/password`：
   *   后端就是这么建的（POST + 动词），而且这是"动作"不是"改字段" ——
   *   要带原密码做二次确认，成功后旧密码立刻失效。
   *
   * ★ 长度规则与**注册**同一套（8-64 位，后端 RegisterBody/ChangePasswordBody
   *   的 message 原文「密码长度需 8-64 位」）。前端在 MeView 里也按同一套常量卡，
   *   免得"按钮亮着、点下去才挨一句服务端报错"。
   *
   * ★ 请求体里**没有** userId：服务端只改 token 本人（JwtAuthGuard + @CurrentUser），
   *   所以前端也不该传 —— 传了反而让人以为能改别人的密码。
   *
   * 失败时抛 ApiError，message 是后端给的中文（如「原密码不正确，请重新输入」），
   * 调用方**原样显示**，不要自己造一句"操作失败"。
   */
  changePassword: (body: { oldPassword: string; newPassword: string }) =>
    request<{ ok: true }>('/auth/change-password', { method: 'POST', body }),

  // ---- 积分 ----
  balance: () => request<BalanceInfo>('/billing/balance'),

  ledger: (take = 20) =>
    request<{
      total: number;
      items: Array<{
        id: string;
        direction: string;
        sourceType: string;
        signedAmount: string;
        balanceAfter: string;
        remark: string | null;
        createdAt: string;
      }>;
    }>(`/billing/ledger?take=${take}`),

  packages: () =>
    request<
      Array<{
        code: string;
        name: string;
        priceCents: number;
        points: number;
        bonusPoints: number;
        isPrimary: boolean;
      }>
    >('/billing/packages'),

  /**
   * 用量与产出汇总（客户看自己的）
   *
   * ⚠️ 服务端**不返回成本**（cost_micro 是平台进货价）——所以这里也没有成本字段，
   *    页面不要"顺手"去别处捞来显示。
   * ⚠️ `images` / `videoSeconds` 为 `null` = 这段时间**没采集到**（2026-10-04 之前的
   *    媒体调用没有这两列），显示成「—」，**不能显示 0**。
   */
  usageSummary: (days = 30) =>
    request<{
      window: { days: number; since: string };
      points: { consumed: string; consumedCount: number; granted: string; grantedCount: number; released: string };
      usage: {
        calls: number;
        images: number | null;
        videoSeconds: number | null;
        inputTokens: number;
        outputTokens: number;
      };
      output: { byType: Record<string, number>; total: number; failedTasks: number };
      byCapability: Array<{
        capability: string;
        calls: number;
        succeeded: number;
        inputTokens: number;
        outputTokens: number;
        images: number | null;
        videoSeconds: number | null;
      }>;
      note: string;
    }>(`/billing/usage-summary?days=${days}`),

  /**
   * ★ 按逐条任务看用量与产出（2026-10-04 加）
   *
   * `title` 可能为 `null` —— 有些中间任务（选题/分镜的一部分）本来就没有标题，
   * 那就**按任务类型显示**，不要拿 id 冒充名字。
   * `images` / `videoSeconds` 为 `null` = 该调用没有采集到用量（不是 0）。
   */
  usageWorks: (days = 30, take = 50, skip = 0) =>
    request<{
      window: { days: number; since: string };
      total: number;
      take: number;
      skip: number;
      items: Array<{
        taskId: string;
        publicId: string;
        taskType: string;
        status: string;
        costTier: string;
        title: string | null;
        relatedType: string | null;
        relatedId: string | null;
        pointsCharged: string;
        createdAt: string;
        finishedAt: string | null;
        usage: {
          calls: number;
          images: number | null;
          videoSeconds: number | null;
          inputTokens: number | null;
          outputTokens: number | null;
        };
      }>;
      note: string;
    }>(`/billing/usage-works?days=${days}&take=${take}&skip=${skip}`),

  /**
   * 导出 CSV（返回**原始字节** + 服务端给的条数元信息）
   *
   * ★ 为什么不是返回字符串：`Response.text()` 按 WHATWG 规定会**吃掉开头的 UTF-8 BOM**，
   *   而 Excel 正是靠这个 BOM 才认得 UTF-8 —— 拿 text() 再 `new Blob()` 出来，
   *   客户下载到的文件在 Excel 里中文就是乱码（服务端明明发了 BOM）。
   *   2026-10-04 实测踩到：接口层用例是绿的（它直接看 HTTP 字节），
   *   但**浏览器里下下来的文件没有 BOM** —— 所以这里必须保留字节。
   *
   * ★ `meta` 是从响应头读的（2026-10-04 加）：导出有行数上限，撞到上限时后端会
   *   丢掉更早的记录。这件事**必须传到客户眼前**，所以后端用响应头说明、
   *   这里读出来交给调用方去提示。
   *
   *   ⚠️ 跨域时这几个头**默认读不到**（浏览器只放行几个"安全"响应头），
   *      必须由服务端在 CORS 里 `exposedHeaders` 出去（见 apps/server/src/main.ts）。
   *      本机开发是 5173 → 3001 的真跨域，所以这条链路本地也得跑通。
   *   ⚠️ 头读不到时数值给 `null`（而不是 0/false 这种"看起来很确定的假值"）：
   *      0 会被读成"这次导出是空的"、false 会被读成"没截断" —— 后者正是我们要
   *      根治的静默截断。分不清就老实说读不到，由调用方决定怎么提示。
   */
  usageExportCsv: async (
    days = 30,
  ): Promise<{
    bytes: ArrayBuffer;
    meta: {
      rows: number | null;
      total: number | null;
      truncated: boolean;
      windowDays: number | null;
    };
  }> => {
    const token = getToken();
    const res = await fetch(`/api/v1/billing/usage-export?days=${days}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError('E-GEN-005', '导出失败，请稍后重试', res.status);
    const bytes = await res.arrayBuffer();

    /** 头读不到 → null（不拿 0 冒充"查过了没有"） */
    const num = (name: string): number | null => {
      const raw = res.headers.get(name);
      if (raw === null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };

    return {
      bytes,
      meta: {
        rows: num('x-export-rows'),
        total: num('x-export-total'),
        /** 只有服务端明确写了 'true' 才算截断 —— 缺头时**不假设**没截断 */
        truncated: res.headers.get('x-export-truncated') === 'true',
        windowDays: num('x-export-window-days'),
      },
    };
  },

  // ---- 生意档案 ----
  createBusiness: (body: {
    name: string;
    industry?: string;
    description?: string;
    products?: string;
    priceCents?: number;
    targetMarket?: string;
    differentiator?: string;
    currentChannels?: string[];
    onCameraWilling?: string;
    weeklyCapacity?: string;
  }) => request<{ id: string; name: string }>('/business', { method: 'POST', body }),

  listBusiness: () =>
    request<Array<{ id: string; name: string; industry: string | null }>>('/business'),

  /**
   * 取单个生意档案的**完整**详情
   *
   * ★ 之前前端没有这个方法，只有 listBusiness —— 而列表页只拿到了
   *   `{id, name, industry}`，表单要回填的十几个字段一个都没有。
   *   这正是 BUG-05「重新打开档案字段空白」的一半原因（另一半是后端 serialize 漏字段）。
   */
  getBusiness: (id: string) => request<BusinessDetail>(`/business/${id}`),

  /**
   * 更新已有档案（局部更新，未传的字段不动）
   *
   * 有了它，编辑态才不会再"变成新建"（BUG-05 的连带问题）。
   */
  updateBusiness: (
    id: string,
    body: {
      name?: string;
      industry?: string;
      description?: string;
      products?: string;
      priceCents?: number;
      targetMarket?: string;
      differentiator?: string;
      currentChannels?: string[];
      onCameraWilling?: string;
      weeklyCapacity?: string;
      preferredForms?: string[];
    },
  ) => request<BusinessDetail>(`/business/${id}`, { method: 'PATCH', body }),

  // ---- 定位（免费钩子） ----
  /**
   * 读**已有的**定位报告（不重新生成、不扣费）
   *
   * 原来只有 generatePositioning（POST，会调模型）。于是"重新打开档案"时
   * 右侧永远显示"还没有定位报告"，即使报告明明在库里 —— 用户只能重新生成一次。
   */
  getPositioning: (businessId: string) =>
    request<Positioning | null>(`/business/${businessId}/positioning`),

  /**
   * 生成定位报告（POST，会调模型；定位免费）
   *
   * ★ 返回类型直接复用 `Positioning`，与 GET 版本**同一个类型**。
   *   原先这里手写了一份内联类型，比实际返回值少了 `id` 等字段 ——
   *   两份"同一个东西"的类型声明，迟早对不上；这次就是它对不上导致的白屏。
   */
  generatePositioning: (businessId: string) =>
    request<Positioning>(`/business/${businessId}/positioning`, { method: 'POST' }),

  // ---- 选题（扣积分） ----
  generateTopics: (businessId: string, count = 8, extra?: string) =>
    request<{
      batchId: string;
      taskId: string;
      pointsCharged: number;
      topics: Array<{
        id: string;
        title: string;
        type: string | null;
        /** null = 模型没给分（不是 0 分）。界面显示「—」 */
        score: number | null;
        reason: string | null;
        contentForm: string | null;
        suggestedDurationSec: number | null;
        suggestedShots: number | null;
        needsCharacterConsistency: boolean;
        /** BUG-11: 推荐度的拆分依据、事实来源、待核实项、待验证假设、争议检测 */
        assessment: TopicAssessment | null;
      }>;
      meta: { modelId: string };
    }>(`/business/${businessId}/topics:generate`, { method: 'POST', body: { count, extra } }),

  /**
   * 选题库列表（分页 / 搜索 / 排序）—— 2026-10-04 修 BUG-A
   *
   * ★ 改之前这个接口**没有分页也没有搜索**，而服务端按推荐分只取前 50 条：
   *   新生成的一批排在 60~70 名 ⇒ 用户花 5 积分生成完，列表里**一条都看不到**，
   *   页面上也没有任何入口能翻到它们（真机复现：106 条选题，新批次 id 全不在返回里）。
   *
   * `sort: 'latest'` 是"按生成时间倒序"—— 刚生成的那批必然在第一页。
   */
  listTopics: (
    businessId: string,
    opts: { page?: number; pageSize?: number; sort?: 'rank' | 'latest'; q?: string } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.page !== undefined) params.set('page', String(opts.page));
    if (opts.pageSize !== undefined) params.set('pageSize', String(opts.pageSize));
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.q) params.set('q', opts.q);
    const qs = params.toString();
    return request<TopicPage>(`/business/${businessId}/topics${qs ? `?${qs}` : ''}`);
  },

  /**
   * BUG-B：读回某条选题**已有的脚本与分镜**（只读，不扣积分）。
   *
   * 脚本与分镜其实早就落库了（`scripts.topic_id` / `shots[].imagePrompt`），
   * 缺的是这个读入口 —— 所以刷新页面后脚本"消失"，再点一次还会再扣 3 积分。
   */
  topicScript: (topicId: string) =>
    request<{
      topicId: string;
      topicStatus: string;
      hasScript: boolean;
      script: ScriptDto | null;
      storyboard: StoryboardDto | null;
    }>(`/topics/${topicId}/script`),

  // ---- 脚本（扣积分 + 合规预检） ----
  /**
   * 由选题生成脚本（扣 3 积分）。
   *
   * ⚠️ 这是**花钱**的那个接口。调用前必须确认"这条选题是不是已经有脚本了" ——
   *   已经有脚本时先弹二次确认（见 TopicsView 的 askScript），
   *   否则用户点两下就被扣两次 3 积分（BUG-B 实测现象）。
   */
  generateScript: (topicId: string, tone?: string) =>
    request<ScriptDto & { meta: { modelId: string; provider: string } }>(`/topics/${topicId}/script`, {
      method: 'POST',
      body: { tone },
    }),

  precheck: (text: string) =>
    request<{
      passed: boolean;
      hitCount: number;
      serialDetected: boolean;
      hits: Array<{ term: string; category: string; message: string; suggestion?: string }>;
    }>('/compliance:precheck', { method: 'POST', body: { text } }),

  /**
   * BUG-11：事实分级复检（免费，不落库）。
   *
   * 与 `precheck` 是两件事，别混：
   *   · precheck      查**违规词**（极限词/绝对化用语），不过审就不能出片；
   *   · claimsCheck   查**事实依据**（就业率/薪资/学费的数字有没有出处），
   *                   它不拦人，只告诉你"这几处得你自己确认"。
   * 传 businessId 时，档案里已出现过的数字会被降级成「你提供的」而不是「待核实」。
   *
   * ⚠️ 路径是 `/compliance/claims-check`（真路径段），**不是** `/compliance:claims-check`。
   *    Express 5 把 `compliance:precheck` 解析成「字面量 compliance + 参数」，
   *    所以那条老路由会匹配 `/compliance` + 任意后缀并**抢先命中**；
   *    新接口写成冒号形式会被它吃掉（返回 201 但响应体是 precheck 的形状）。
   */
  claimsCheck: (text: string, businessId?: string | null) =>
    request<{
      claims: ClaimItem[];
      summary: ClaimSummary;
      checkedAt: string;
      factsUsed: string | null;
      note: string;
    }>('/compliance/claims-check', { method: 'POST', body: { text, businessId: businessId ?? undefined } }),

  // ---- 对标分析 ----
  benchmarkSources: () =>
    request<{
      isProduction: boolean;
      sources: Array<{
        name: string;
        label: string;
        commerciallyLicensed: boolean;
        enabled: boolean;
        available: boolean;
        reason?: string;
      }>;
    }>('/benchmark/sources'),

  /** 抓取并分析对标账号（免费） */
  benchmarkAnalyze: (body: { input: string; businessId?: string | null; refresh?: boolean; limit?: number }) =>
    request<BenchmarkAnalyzeResult>('/benchmark/analyze', { method: 'POST', body }),

  benchmarkAccounts: () =>
    request<
      Array<{
        id: string;
        platform: string;
        platformUid: string;
        nickname: string | null;
        uniqueId: string | null;
        avatar: string | null;
        followers: number | null;
        awemeCount: number | null;
        worksCount: number;
        hitCount: number;
        note: string | null;
        fetchedAt: string | null;
        fetchFailed: boolean;
        fetchError: string | null;
        createdAt: string;
      }>
    >('/benchmark/accounts'),

  benchmarkAccount: (id: string) => request<BenchmarkAccountDetail>(`/benchmark/accounts/${id}`),

  /**
   * 手动录入对标样本（BUG-09）
   *
   * ★ 后端早就有这个接口（`POST /benchmark/accounts/manual`），
   *   但前端一直没有对应方法 —— 于是页面提示「已切换为手动录入模式」
   *   却没有任何表单可以填，用户被卡在一个空页面上。
   *   这就是报告里 BUG-09 的完整成因。
   *
   * 用途：自动抓取不可用时（无可商用数据源 / 平台不支持 / 抓取失败），
   * 让用户把自己看到的同行数据填进来，继续做分析。
   * 服务端会把来源标成 manual 并记录采集时间（报告要求：样本要标来源）。
   */
  benchmarkManual: (body: {
    /** 账号标识：抖音号 / 主页链接 / 自定义名称都行 */
    platformUid: string;
    nickname?: string;
    followers?: number;
    note?: string;
    businessId?: string | null;
    works?: Array<{
      desc: string;
      likes?: number;
      comments?: number;
      collects?: number;
      shares?: number;
      publishedAt?: string;
    }>;
  }) => request<{ accountId: string; worksCount: number; source: string }>('/benchmark/accounts/manual', {
    method: 'POST',
    body,
  }),

  /** AI 拆解：把爆款结构提炼成可迁移公式（扣积分） */
  benchmarkInsights: (id: string) =>
    request<{
      accountId: string;
      accountSummary: string;
      contentPillars: string[];
      opportunities: Array<{ label: string; tone: 'success' | 'warning' | 'danger' }>;
      explainedCount: number;
      totalWorks: number;
      pointsCharged: number;
      modelId: string;
      // BUG-16: costYuan 已从用户接口移除（平台侧见管理端诊断）
    }>(`/benchmark/accounts/${id}/insights`, { method: 'POST' }),

  benchmarkRemove: (id: string) => request<{ ok: true }>(`/benchmark/accounts/${id}`, { method: 'DELETE' }),

  // ---- 生成中心 ----
  /**
   * 任务列表（分页口径见 `TaskPage`；`cursor` 传上一页的 `nextCursor`）。
   *
   * ⚠️ 返回的是**对象**（`{items,total,take,hasMore,nextCursor}`），调用方读 `.items`。
   *
   * ★ `from` / `to` 是**北京时间的那一天**（`YYYY-MM-DD`，含首含尾）——
   *   "找上个月那条"用。日期格式不对服务端会回 400 并说清怎么写（**不会静默忽略**：
   *   静默忽略会让人以为筛选生效了、其实返回的是全部）。
   */
  tasks: (opts: { status?: 'running' | 'done' | 'failed'; take?: number; cursor?: string | null; from?: string; to?: string } = {}) =>
    request<TaskPage>(
      `/tasks?take=${opts.take ?? 30}${opts.status ? `&status=${opts.status}` : ''}${
        opts.cursor ? `&cursor=${encodeURIComponent(opts.cursor)}` : ''
      }${opts.from ? `&from=${opts.from}` : ''}${opts.to ? `&to=${opts.to}` : ''}`,
    ),

  /** 单个任务详情 —— 异步出片时前端靠它轮询进度与取产出 */
  task: (id: string) => request<GenerationTaskDetailDto>(`/tasks/${id}`),
  taskStats: () =>
    request<{ running: number; succeeded: number; failed: number; total: number }>('/tasks/stats'),

  taskDetail: (id: string) => request<GenerationTaskDetailDto>(`/tasks/${id}`),

  taskCancel: (id: string) => request<{ ok: true }>(`/tasks/${id}/cancel`, { method: 'POST' }),

  /**
   * BUG-15：按**原任务的原始参数**重试。
   * 幂等：同一原始请求已发起过重试时后端返回 400，不会重复扣费。
   */
  taskRetry: (id: string) =>
    request<{ ok: true; taskType: string; newTaskId: string | null; pointsCharged: number; message: string }>(
      `/tasks/${id}/retry`,
      { method: 'POST' },
    ),

  // ---- 智能体：先出方案，确认后才扣费 ----
  /**
   * 生成制作计划（免费，不扣费）。
   *
   * eviseOf = **修改哪条已有计划**（BUG-04 B1）。
   * 报告要求「用户补充信息后修改原计划，不新造另一个出片订单」：
   * 传了它，后端会沿用原会话、把原计划置为 superseded（再也确认不了），
   * 并给新计划记上 revision 链。
   */
  agentPlan: (body: { message: string; businessId?: string | null; conversationId?: string | null; reviseOf?: string | null }) =>
    request<AgentPlanDto & { conversationId: string }>('/agent/plan', { method: 'POST', body }),

  agentPlans: (take = 20) => request<AgentPlanDto[]>(`/agent/plans?take=${take}`),

  agentPlanDetail: (id: string) => request<AgentPlanDto>(`/agent/plans/${id}`),

  // ---- 会话（BUG-06/07）----
  /** 会话列表（带末条消息预览） */
  agentConversations: (take = 30) => request<ConversationSummary[]>(`/agent/conversations?take=${take}`),

  /**
   * 打开一个会话：消息 + 该会话下的全部计划
   *
   * ★ 这是"点历史打开原会话而不是追加到当前对话"的服务端入口。
   *   消息与计划都由 conversationId 过滤，天然隔离。
   */
  agentConversation: (id: string) => request<ConversationDetail>(`/agent/conversations/${id}`),

  /** 重命名 / 归档会话 */
  agentPatchConversation: (id: string, body: { title?: string; status?: 'active' | 'archived' }) =>
    request<{ id: string; title: string; status: string }>(`/agent/conversations/${id}`, {
      method: 'PATCH',
      body,
    }),

  /** 确认制作 —— 此处才真正执行并扣费。重复调用不会重复扣 */
  agentConfirm: (id: string) =>
    request<AgentPlanDto & { results: AgentStepResult[]; pointsCharged: number; alreadyConfirmed: boolean }>(
      `/agent/plans/${id}/confirm`,
      { method: 'POST' },
    ),

  agentCancel: (id: string) => request<{ ok: true }>(`/agent/plans/${id}/cancel`, { method: 'POST' }),

  /**
   * BUG-08：只读校验这份报价是否仍然成立（不扣费、不改状态）
   *
   * 前端在渲染计划卡片时调用：`valid=false` 就把「确认制作」置灰并显示原因，
   * 用户不必点一下才发现前提已经变了。
   * 后端在 confirm() 时会用**同一段逻辑**再校验一次，所以绕过前端也没用。
   */
  agentQuoteCheck: (id: string) =>
    request<{
      planId: string;
      planVersion: number;
      status: string;
      valid: boolean;
      changed: string[];
      message: string;
      checkable: boolean;
    }>(`/agent/plans/${id}/quote-check`),

  // ---- 内容生产（M5 分镜 → M6 出片 → M7 导出 → M8 复盘）----
  /**
   * 当前环境能出什么：分能力如实报「真模型 / Mock / 无」
   *
   * ★ `tiers`（2026-10-04 加）：每个能力**在哪些档位上真有通道**。
   *   只有 `providers` 是不够的 —— 一条通道可能只支持部分档位
   *   （火山方舟出图没有精品档、出视频没有经济档）。
   *   不看这一项，就会出现"选了精品档、按钮也可点，确认后才失败"。
   */
  productionCapabilities: () =>
    request<{
      llm: { kind: 'real' | 'mock' | 'none'; providers: string[]; tiers?: string[] };
      image: { kind: 'real' | 'mock' | 'none'; available: boolean; providers: string[]; tiers?: string[] };
      video: { kind: 'real' | 'mock' | 'none'; available: boolean; providers: string[]; tiers?: string[] };
      compose: { available: boolean; bin: string; reason?: string; concurrency?: { running: number; queued: number; limit: number } };
      /**
       * 出片链路的并发与同步预算 —— 前端据此提前告诉用户"要等多久"、
       * 以及"这一单会不会被同步上限拒掉"（与后端那道闸门用的是**同一个估算函数**，
       * 见 @gfc/shared 的 estimateProduceSeconds）。
       */
      produce?: {
        imageConcurrency: number;
        videoConcurrency: number;
        /** 同步请求的耗时预算（秒）；估算超过它，后端会在扣费前拒绝（E-PROD-010） */
        inlineBudgetSec: number;
        secondsPerImage: number;
      };
      slideshowReady: boolean;
      videoReady: boolean;
      /** 画面是否为占位图（图片或视频通道是 Mock） */
      placeholderMedia: boolean;
      note: string;
    }>('/production/capabilities'),

  /**
   * 生成分镜（扣 10 积分）。
   *
   * ⚠️ 同样是花钱的接口：库里**已经存过分镜**时（`script.shots[].imagePrompt`），
   *   界面必须先告诉用户"已经有分镜了，重新生成会再扣 10 积分"。
   */
  generateStoryboard: (scriptRef: string) =>
    request<StoryboardDto>(`/scripts/${scriptRef}/storyboard`, { method: 'POST' }),

  /**
   * 出片（分镜 → 出图 → 合成 → 写 AI 标识 → 落作品）
   *
   * ★ 返回值有**两种形态**，调用方必须判别（见 isQueuedProduce）：
   *
   *   同步（后端 ASYNC_PRODUCTION≠true）：
   *     阻塞 70+ 秒，直接返回作品 DTO（有 workId / videoUrl）。
   *
   *   异步（后端开了队列且 worker 在跑）：
   *     立刻返回 `{ queued: true, taskId, pollUrl }`，**没有 workId**。
   *     此时必须拿 taskId 去轮询 `api.task(taskId)`，
   *     等 status 变成 succeeded 后从 `output` 里取成品。
   *
   *   后端默认走同步，所以这段兼容性不是为了"可能发生"，
   *   而是为了让运维**随时能安全地打开异步开关**而不炸前端。
   */
  produce: (
    scriptRef: string,
    body: {
      mode?: 'slideshow' | 'video';
      costTier?: 'economic' | 'standard' | 'premium';
      ratio?: string;
      /**
       * 逐镜头选视频（2026-10-08）：只给这些镜头（1-based 镜头号）出 AI 视频，其余保持静帧。
       * 不传时按 `mode` 走（video = 全部出视频、slideshow = 一个都不出）。
       */
      videoShotNos?: number[];
      /**
       * 逐镜头用真实素材（2026-10-08）：`[{ no: 镜头号, assetId: 素材 id }]`。
       * 这些镜头**直接拿素材当画面**，不出图也不计图片费（③「真实素材优先复用」）。
       * 与 `videoShotNos` 互斥（一个真拍、一个生成）。
       */
      assetShots?: Array<{ no: number; assetId: string }>;
    },
  ) =>
    request<ProduceResult>(`/scripts/${scriptRef}/produce`, { method: 'POST', body }),

  /**
   * 作品库（分页口径见 `WorkPage`；`cursor` 传上一页的 `nextCursor`）。
   *
   * ⚠️ 返回的是**对象**（`{items,total,take,hasMore,nextCursor}`），调用方读 `.items`。
   *    改之前是裸数组 + 静默取前 30 条；再往前一版是"提档到 100 就停"（第 100 条之后够不到）。
   */
  works: (filter?: 'exportable' | 'blocked', take = 30, cursor?: string | null) =>
    request<WorkPage>(
      `/works?take=${take}${filter ? `&filter=${filter}` : ''}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),

  work: (id: string) => request<WorkDetailDto>(`/works/${id}`),

  /**
   * 导出到平台 —— 只有 AI 标识齐备**且**已勾选网信办必选内容标签才放行
   *
   * contentLabels 不是可选项：中央网信办 2026-05-12 规定发布必须勾选 6 类标签之一，
   * 后端会硬拦（E-COMP-009）；剧情广告片还要确认法定告知话术（E-COMP-010）。
   */
  workExport: (
    id: string,
    platform: string,
    compliance: { contentLabels: string[]; disclaimersAcknowledged?: boolean },
  ) =>
    request<{
      workId: string;
      platform: string;
      platformLabel: string;
      fileUrl: string;
      ratio: string;
      width: number;
      height: number;
      refit: boolean;
      resolutionNote: string | null;
      aiLabelStatus: string;
      platformNote: string;
      commerceNote: string | null;
      /** 已勾选的标签文案，导出结果页照着提示用户 */
      contentLabels: Array<{ key: string; label: string }>;
      /** 剧情广告片必须随片附上的法定告知话术 */
      storyAdDisclaimers: string[];
    }>(`/works/${id}/export`, {
      method: 'POST',
      body: { platform, ...compliance },
    }),

  workEffect: (id: string, body: { views?: number; likes?: number; conversions?: number; note?: string }) =>
    request<WorkDto>(`/works/${id}/effect`, { method: 'POST', body }),

  workRemove: (id: string) => request<{ ok: true }>(`/works/${id}`, { method: 'DELETE' }),

  // ---- 一键分发（半自动） ----
  /**
   * 这条作品在各平台**能不能直接发**（时长/画幅体检）。
   *
   * 纯规则、免费。界面一打开就该显示 —— 免得商家传上去被拒了
   * 才知道小红书上限 60 秒、B 站要 16:9。
   */
  platformFit: (workId: string) => request<PlatformFit[]>(`/works/${workId}/platform-fit`),

  /** 某条作品已有的分发记录 */
  workDistributions: (workId: string) => request<DistributionDto[]>(`/works/${workId}/distributions`),

  /**
   * 生成（或重新生成）若干平台的发布包。
   * 免费 —— 用户已经为片子付过费了，最后一步不该再设卡。
   */
  generatePacks: (workId: string, platforms: string[]) =>
    request<{
      workId: string;
      generated: string[];
      /** 模型漏掉的平台。**必须显示给用户**，不能悄悄少给一个 */
      missing: string[];
      distributions: DistributionDto[];
      fits: PlatformFit[];
      aiLabelReady: boolean;
      warnings: string[];
    }>(`/works/${workId}/distributions`, { method: 'POST', body: { platforms } }),

  /** 最近的分发记录（分发中心） */
  /**
   * 分发中心：跨作品的分发记录
   *
   * ★ 三个数字口径不一样，界面必须分开说（2026-10-04 修「chip 说 3 条、屏幕 4 行」时定的）：
   *   · `items`  —— 本次返回的那些行，每条带 `work`（是哪条片子；作品被删时为 null）
   *   · `counts` —— **全局**按状态的总数（不带 status/platform 筛选），是"你一共有多少条待发"的背景板
   *   · `total`  —— **当前筛选下**的总数
   *   · `take`   —— 服务端**实际生效**的一页大小（请求值被夹在 1..100）
   *
   * ★ 分页是**键集游标**（波次 11，与作品库/生成中心/工单同一套）：
   *   `truncated` 这个名字**已退休**，改成 `hasMore` + `nextCursor`；
   *   非法游标由服务端回 400（不会静默回第一页）。
   */
  distributions: (params: { take?: number; status?: string; platform?: string; cursor?: string | null } = {}) =>
    request<{
      items: Array<DistributionDto & { work: { id: string; title: string; ratio: string | null; durationSec: number | null } | null }>;
      counts: Record<string, number>;
      /** 当前筛选下的总数（不是全局、也不是本次返回条数） */
      total: number;
      /** 服务端实际生效的一页大小 */
      take: number;
      /** 还有更早的一页（累积列表据此决定要不要显示「加载更早的」） */
      hasMore: boolean;
      /** 不透明游标：原样回传取下一页 */
      nextCursor: string | null;
      filtered: boolean;
    }>(
      `/distributions?take=${params.take ?? 50}${params.status ? `&status=${params.status}` : ''}${
        params.platform ? `&platform=${params.platform}` : ''
      }${params.cursor ? `&cursor=${encodeURIComponent(params.cursor)}` : ''}`,
    ),

  /**
   * 用户确认「我发出去了」。
   * ★ 只有这个动作能把状态变成 published —— 系统不会替他声明已发布。
   */
  markPublished: (id: string, body: { publishedUrl?: string; note?: string }) =>
    request<DistributionDto>(`/distributions/${id}/published`, { method: 'POST', body }),

  /** 标记不发 / 发布失败（原因必填 —— "为什么没发"是复盘最想知道的） */
  markDistributionStatus: (id: string, body: { status: 'skipped' | 'failed' | 'pending'; note?: string }) =>
    request<DistributionDto>(`/distributions/${id}/status`, { method: 'POST', body }),

  /** 我登记过的各平台账号 */
  platformAccounts: () => request<PlatformAccountDto[]>('/distribution/accounts'),

  // ---- 数据复盘（M8：作品日指标）----
  /**
   * 登记（或覆盖）某条作品在某平台某一天的数据。
   * `(作品, 平台, 日期)` 唯一 ⇒ 同一天重复提交是**更新**，不会堆重复行。
   */
  recordWorkMetric: (
    workId: string,
    body: {
      platform: string;
      metricDate: string;
      playCount?: number;
      likeCount?: number;
      commentCount?: number;
      shareCount?: number;
      collectCount?: number;
      followerDelta?: number;
      conversionCount?: number;
      conversionNote?: string;
    },
  ) => request<WorkMetricDto>(`/work-metrics/works/${workId}`, { method: 'POST', body }),

  /** 明细列表（键集游标，与其它列表同一套口径） */
  workMetrics: (
    params: { workId?: string; platform?: string; from?: string; to?: string; take?: number; cursor?: string | null } = {},
  ) =>
    request<{
      items: Array<WorkMetricDto & { work: { id: string; title: string } | null }>;
      total: number;
      take: number;
      hasMore: boolean;
      nextCursor: string | null;
      filtered: boolean;
    }>(
      `/work-metrics?take=${params.take ?? 50}${params.workId ? `&workId=${params.workId}` : ''}${
        params.platform ? `&platform=${params.platform}` : ''
      }${params.from ? `&from=${params.from}` : ''}${params.to ? `&to=${params.to}` : ''}${
        params.cursor ? `&cursor=${encodeURIComponent(params.cursor)}` : ''
      }`,
    ),

  /** 近 N 个业务日的看板（按平台汇总 + 逐日序列，没数据的天补 0） */
  workMetricsSummary: (days = 30) =>
    request<{
      days: number;
      from: string;
      to: string;
      total: { playCount: number; conversionCount: number; followerDelta: number; entries: number };
      byPlatform: Array<{
        platform: string;
        label: string;
        playCount: number;
        conversionCount: number;
        followerDelta: number;
        entries: number;
      }>;
      series: Array<{ day: string; playCount: number; conversionCount: number; entries: number }>;
      daysWithData: number;
      hasData: boolean;
    }>(`/work-metrics/summary?days=${days}`),

  /**
   * **批量导入**（P1 手动回流）：把平台后台导出/复制的整张表粘进来。
   *
   * `dryRun=true` 只预览（会新建几条/更新几条/哪几行有问题），**一个字都不写**。
   * 部分成功是允许的，但**逐行报告**：成功的照常写入，有问题的进 `failed` 并给原因与行号。
   */
  importWorkMetrics: (csv: string, dryRun = false) =>
    request<{
      dryRun: boolean;
      total: number;
      created: number;
      updated: number;
      failed: Array<{ row: number; reason: string }>;
      detectedDelimiter: string;
      results: Array<{ row: number; action: 'created' | 'updated'; id?: string }>;
    }>('/work-metrics/import', { method: 'POST', body: { csv, dryRun } }),

  /** 删掉一条登记（记错了撤掉） */
  deleteWorkMetric: (id: string) => request<{ ok: true; deleted: string }>(`/work-metrics/${id}`, { method: 'DELETE' }),

  /**
   * **归因**：哪一类内容更有效（按内容形态 / 选题类型 / 平台分组）
   *
   * ⚠️ 读这个接口的界面必须同时显示 `samples`（登记次数）与 `works`（涉及作品数）：
   *   同一条作品记 10 天 = 10 个样本，但那只是**一条片子**。
   *   `groups` 是够格下结论的，`weak` 是样本不足/只涉及 1 条作品的（**不进结论**）。
   */
  workMetricsAttribution: (days = 30) =>
    request<{
      days: number;
      from: string;
      to: string;
      minSamples: number;
      totalRows: number;
      linkedRows: number;
      unlinkedRows: number;
      hasData: boolean;
      note: string;
      dimensions: Array<{
        key: string;
        label: string;
        groups: Array<{
          key: string;
          label: string;
          samples: number;
          works: number;
          plays: number;
          conversions: number;
          avgPlay: number;
          convPer1k: number | null;
        }>;
        weak: Array<{
          key: string;
          label: string;
          samples: number;
          works: number;
          plays: number;
          conversions: number;
          avgPlay: number;
          convPer1k: number | null;
          reason: string;
        }>;
      }>;
    }>(`/work-metrics/attribution?days=${days}`),

  /**
   * 登记一个平台账号。
   *
   * ★ 「分发」的前提是先绑号 —— 这个接口一直在，但界面上一度**没有任何入口**
   *   （全仓 `platformAccounts` 零 .vue 调用），客户根本没地方绑。
   */
  createPlatformAccount: (body: { platform: string; nickname: string; profileUrl?: string; verified?: boolean }) =>
    request<PlatformAccountDto>('/distribution/accounts', { method: 'POST', body }),

  /**
   * 改平台账号：重命名 / 改主页链接 / **设为默认号**（`isDefault: true`）。
   * 同一个平台只能有一个默认号 —— 服务端会把同平台其它号先清掉。
   */
  updatePlatformAccount: (
    id: string,
    body: { nickname?: string; profileUrl?: string; verified?: boolean; isDefault?: boolean },
  ) => request<PlatformAccountDto>(`/distribution/accounts/${id}`, { method: 'PATCH', body }),

  removePlatformAccount: (id: string) => request<{ ok: true }>(`/distribution/accounts/${id}`, { method: 'DELETE' }),

  // ---- 技能库 ----
  skills: (businessId?: string | null) =>
    request<{
      groups: Array<{ category: string; label: string; skills: SkillDto[] }>;
      hasBusiness: boolean;
      benchmarkCount: number;
    }>(`/skills${businessId ? `?businessId=${businessId}` : ''}`),

  /** 从一句话猜技能（模型不可用时的兜底 / 输入联想） */
  skillSuggest: (text: string) => request<{ key: string; name: string; confidence: string } | null>('/skills/suggest', { method: 'POST', body: { text } }),

  runSkill: (
    key: string,
    body: {
      businessId?: string | null;
      benchmarkAccountId?: string;
      topicId?: string;
      scriptRef?: string;
      extra?: string;
    },
  ) =>
    request<{
      skill: { key: string; name: string; category: string };
      steps: Array<{ action: string; label: string; ok: boolean; message: string; data?: unknown }>;
      completed: number;
      total: number;
      success: boolean;
      pointsCharged: number;
      estimatedPoints: number;
      output: { topicId: string | null; scriptId: string | null; workId: string | null };
    }>(`/skills/${key}/run`, { method: 'POST', body }),

  // ---- 爆款解析复刻 ----
  /**
   * 拆解一条爆款并产出复刻脚本。
   *
   * ⚠️ `text` 是**必填**的：我们没有平台作品数据接口，`sourceUrl` 只作记录、打不开。
   *    只给链接会被后端 400 挡回来，并把原因写在 message 里 —— 前端要原样显示，
   *    不要自己造一句"网络错误"。
   */
  remixBreakdown: (body: {
    text: string;
    title?: string;
    platform?: string;
    durationSec?: number;
    sourceUrl?: string;
    businessId?: string | null;
  }) => request<DeliverableDto>('/remix/breakdown', { method: 'POST', body }),

  /** 复刻历史 */
  remixList: (take = 30) =>
    request<Array<{ publicId: string; title: string; status: string; createdAt: string }>>(`/remix?take=${take}`),

  /** 读一份复刻成果 */
  remixGet: (id: string) => request<DeliverableDto>(`/deliverables/${id}`),

  // ---- 文字交付成果（营销方案 / 直播方案 / 跟进 SOP / 数据复盘 / 爆款复刻）----
  /**
   * 交付成果列表（某个生意档案下）
   *
   * ⚠️ 之前**没有这个页面**：智能体在对话里只回一句摘要（"生成营销方案《…》，6 个小节"），
   *    正文根本看不到 —— 用户花了 8 积分拿到的东西读不了。交付中心就是补这个洞。
   */
  deliverables: (businessId: string, take = 50) =>
    request<
      Array<{ publicId: string; planKind: string; planKindLabel: string; title: string; status: string; createdAt: string }>
    >(`/business/${businessId}/deliverables?take=${take}`),

  /** 读一份交付成果的全文 */
  deliverable: (id: string) => request<DeliverableDto>(`/deliverables/${id}`),

  // ---- 充值（下单 / 查单 / 通道状态）----
  /**
   * 支付通道状态（无需登录）
   *
   * 前端**必须**用它来决定"能不能真的充值"，不许写死"支付通道开发中" ——
   * 写死意味着配好商户号那天还得改一次代码，而且中间那段时间界面在骗人。
   */
  payChannels: () => request<PayChannelsDto>('/billing/pay-channels'),

  /** 下单（金额由服务端从数据库套餐取，客户端传不了金额） */
  createRechargeOrder: (packageCode: string) =>
    request<RechargeOrderCreated>('/billing/orders', { method: 'POST', body: { packageCode } }),

  /** 查单（支付后回来刷新状态用） */
  rechargeOrder: (orderNo: string) => request<RechargeOrderDto>(`/billing/orders/${encodeURIComponent(orderNo)}`),

  /** 模拟支付：**仅测试环境**（生产直接 403）。调用前必须确认 sandbox=true */
  mockPayOrder: (orderNo: string) =>
    request<unknown>(`/billing/orders/${encodeURIComponent(orderNo)}/mock-pay`, { method: 'POST', body: {} }),

  // ---- 售后工单 ----
  /**
   * 提工单
   *
   * 标题与描述都有最小长度（服务端也是）：含糊的工单会来回好几轮，
   * 与其让用户写一句"坏了"再问三遍，不如一次说清。
   */
  createTicket: (body: { category: string; title: string; body: string; relatedType?: string; relatedId?: number }) =>
    request<SupportTicketDto>('/support/tickets', { method: 'POST', body }),

  /**
   * 我的工单列表
   *
   * ⚠️ 返回的是 `{ items, total, take, hasMore, nextCursor }` 而不是数组：
   *    单次窗口装不下时，界面靠 `nextCursor` **继续往下翻**（波次 9 从"提档到 100 就停"
   *    改成键集游标 —— 那一版第 100 条之后永远够不到）。
   * `status` 是筛选；`cursor` 传上一页的 `nextCursor`（不透明字符串，别解析）。
   */
  tickets: (take = 50, status?: string, cursor?: string | null) =>
    request<SupportTicketList>(
      `/support/tickets?take=${take}${status ? `&status=${encodeURIComponent(status)}` : ''}${
        cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
      }`,
    ),

  /**
   * 工单概览（顶栏提醒用）
   *
   * ⚠️ `waitingForUser` 的含义是"**球在你这边**"（我们回过了，等你），
   *    不是"未读"—— 我们没有做已读状态。界面文案要照着这个说。
   */
  supportSummary: () =>
    request<{
      open: number;
      replied: number;
      closed: number;
      total: number;
      waitingForUser: number;
      waitingForPlatform: number;
    }>('/support/summary'),

  ticket: (id: string) => request<SupportTicketDto>(`/support/tickets/${encodeURIComponent(id)}`),

  replyTicket: (id: string, body: string) =>
    request<SupportTicketDto>(`/support/tickets/${encodeURIComponent(id)}/messages`, { method: 'POST', body: { body } }),

  /** 问题解决了，自己关掉（不用等我们） */
  closeTicket: (id: string) =>
    request<SupportTicketDto>(`/support/tickets/${encodeURIComponent(id)}/close`, { method: 'POST', body: {} }),

  /**
   * 上传工单附件（multipart）
   *
   * 不能走 `request()`：那个函数固定 `content-type: application/json`，
   * 上传必须让浏览器自己带 boundary。所以与 uploadAsset 一样直接用 fetch。
   */
  uploadTicketAttachment: async (ticketId: string, file: File): Promise<SupportAttachmentDto> => {
    const fd = new FormData();
    fd.append('file', file);
    const token = getToken();
    const res = await fetch(`/api/v1/support/tickets/${encodeURIComponent(ticketId)}/attachments`, {
      method: 'POST',
      headers: token ? { authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const text = await res.text();
    let json: { success?: boolean; data?: SupportAttachmentDto; code?: string; message?: string } | null = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      throw new ApiError('E-GEN-005', '服务返回格式异常', res.status);
    }
    if (!res.ok || json?.success === false) {
      throw new ApiError(json?.code ?? 'E-GEN-005', json?.message ?? '附件上传失败', res.status);
    }
    if (!json?.data) throw new ApiError('E-GEN-005', '服务没有返回上传结果', res.status);
    return json.data;
  },

  /**
   * 把附件拉成 Blob（预览 / 下载都走它）
   *
   * 为什么不直接用 `<img src="...">` 或 `<a href="...">`：
   *   下载接口要 `Authorization` 头，而浏览器发图片/链接请求时**带不上自定义头**。
   *   所以只能先 fetch 成 Blob，再 `URL.createObjectURL` 给 img / 触发下载。
   */
  ticketAttachmentBlob: async (attachmentId: string): Promise<Blob> => {
    const token = getToken();
    const res = await fetch(`/api/v1/support/attachments/${encodeURIComponent(attachmentId)}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let msg = '附件下载失败';
      try {
        const j = (await res.json()) as { message?: string };
        if (j?.message) msg = j.message;
      } catch {
        /* 非 JSON（比如 502 页面）就用默认文案 */
      }
      throw new ApiError('E-GEN-005', msg, res.status);
    }
    return res.blob();
  },

  // ---- 素材与一致性资产 ----
  assets: (kind?: string, take = 60) =>
    request<AssetDto[]>(`/assets?take=${take}${kind ? `&kind=${kind}` : ''}`),

  assetStats: () =>
    request<{ counts: Record<string, number>; labels: Record<string, string>; total: number }>('/assets/stats'),

  /** 只返回已登记授权的一致性角色（未授权的不会被生成流程用到） */
  usableCharacters: () => request<AssetDto[]>('/assets/characters/usable'),

  asset: (id: string) => request<AssetDto>(`/assets/${id}`),

  /**
   * 上传素材（multipart）
   *
   * 不能走 request()：那个函数固定 content-type: application/json，
   * 上传必须让浏览器自己带 boundary。所以这里直接用 fetch。
   */
  uploadAsset: async (
    file: File,
    meta: { kind: string; name?: string; consentType?: string; consentNote?: string; appearancePrompt?: string; businessId?: string | null },
  ): Promise<AssetDto> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', meta.kind);
    if (meta.name) fd.append('name', meta.name);
    if (meta.consentType) fd.append('consentType', meta.consentType);
    if (meta.consentNote) fd.append('consentNote', meta.consentNote);
    if (meta.appearancePrompt) fd.append('appearancePrompt', meta.appearancePrompt);
    if (meta.businessId) fd.append('businessId', meta.businessId);

    const token = getToken();
    const res = await fetch('/api/v1/assets/upload', {
      method: 'POST',
      headers: token ? { authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const text = await res.text();
    let json: { success?: boolean; data?: AssetDto; code?: string; message?: string } | null = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      throw new ApiError('E-GEN-005', '服务返回格式异常', res.status);
    }
    if (!res.ok || json?.success === false) {
      throw new ApiError(json?.code ?? 'E-GEN-005', json?.message ?? '上传失败', res.status);
    }
    if (!json?.data) {
      throw new ApiError('E-GEN-005', '服务没有返回上传结果', res.status);
    }
    return json.data;
  },

  assetFromWork: (workId: string, body: { kind?: string; name?: string }) =>
    request<AssetDto>(`/assets/from-work/${workId}`, { method: 'POST', body }),

  assetUpdate: (
    id: string,
    body: { name?: string; tags?: string[]; appearancePrompt?: string; consentType?: string; consentNote?: string },
  ) => request<AssetDto>(`/assets/${id}`, { method: 'PATCH', body }),

  assetRemove: (id: string) => request<{ ok: true }>(`/assets/${id}`, { method: 'DELETE' }),

  // ============================================================
  //  账号经营（P1：经营账号 / 策略版本 / 内容栏目 / 周计划）
  //
  //  ★ 命名纪律：这里的「账号」指**经营账号**（面向某类客户的品牌/IP），
  //    不是「平台账号」（抖音授权身份），也不是「企业空间」（租户）。
  //    四者混叫是本仓库明确禁止的（见 docs/重构方案/P0-工程契约.md §1）。
  // ============================================================

  operationAccounts: () => request<{ items: OperationAccountDto[]; nextCursor: string | null }>('/operation/accounts'),

  operationAccountCreate: (body: { name: string; audience?: string; goal?: string; note?: string }) =>
    request<OperationAccountDto>('/operation/accounts', { method: 'POST', body }),

  operationAccount: (id: string) => request<OperationAccountDetail>('/operation/accounts/' + id),

  operationAccountUpdate: (id: string, body: { name?: string; audience?: string; goal?: string; status?: string; note?: string }) =>
    request<OperationAccountDto>('/operation/accounts/' + id, { method: 'PATCH', body }),

  /** 待整理：`accountId IS NULL` 的历史平台账号与资产（**不会**被静默挂到任何账号上） */
  operationUnassigned: (id: string) =>
    request<{ platformAccounts: PlatformAccountDto[]; assets: AssetDto[] }>('/operation/accounts/' + id + '/unassigned'),

  operationAssign: (id: string, body: { platformAccountIds?: string[]; assetIds?: string[] }) =>
    request<{ ok: true; assigned: number }>('/operation/accounts/' + id + '/assign', { method: 'POST', body }),

  /** 当前**已批准**的策略；`strategy` 为 null 时 `needsStrategy=true`，前端据此出引导 */
  operationCurrentStrategy: (id: string) =>
    request<{ strategy: OperationStrategyDto | null; needsStrategy: boolean }>('/operation/accounts/' + id + '/strategy'),

  operationStrategies: (id: string) =>
    request<{ items: OperationStrategyDto[] }>('/operation/accounts/' + id + '/strategies'),

  operationStrategyCreate: (
    id: string,
    body: {
      positioning?: unknown;
      pillars?: unknown;
      cadence?: unknown;
      /** 周预算上限 —— 积分（与 weeklyBudgetMicro **成对**，两个单位不许混写） */
      weeklyBudgetPoints?: number;
      /** 周预算上限 —— 微元 */
      weeklyBudgetMicro?: string;
      note?: string;
    },
  ) => request<OperationStrategyDto>('/operation/accounts/' + id + '/strategies', { method: 'POST', body }),

  operationStrategyUpdate: (id: string, body: Record<string, unknown>) =>
    request<OperationStrategyDto>('/operation/strategies/' + id, { method: 'PATCH', body }),

  /** 批准策略：同事务把该账号旧的 approved 置 superseded；已批准再点会被拒（400） */
  operationStrategyApprove: (id: string) =>
    request<OperationStrategyDto>('/operation/strategies/' + id + '/approve', { method: 'POST' }),

  operationColumns: (id: string) =>
    request<{ items: OperationColumnDto[] }>('/operation/accounts/' + id + '/columns'),

  operationColumnCreate: (
    id: string,
    body: { name: string; purpose?: string; weight?: number; sortOrder?: number },
  ) => request<OperationColumnDto>('/operation/accounts/' + id + '/columns', { method: 'POST', body }),

  operationColumnUpdate: (id: string, body: Record<string, unknown>) =>
    request<OperationColumnDto>('/operation/columns/' + id, { method: 'PATCH', body }),

  operationWeeklyPlans: (id: string) =>
    request<{ items: OperationWeeklyPlanDto[]; nextCursor: string | null }>('/operation/accounts/' + id + '/weekly-plans'),

  /** 不传 weekStartDate 时后端取 **Asia/Shanghai 的本周周一**；同账号同周重复建返回 409 */
  operationWeeklyPlanCreate: (
    id: string,
    body: { weekStartDate?: string; budgetPoints?: number; budgetMicro?: string },
  ) => request<OperationWeeklyPlanDto>('/operation/accounts/' + id + '/weekly-plans', { method: 'POST', body }),

  operationWeeklyPlan: (id: string) => request<OperationWeeklyPlanDetail>('/operation/weekly-plans/' + id),

  operationWeeklyPlanConfirm: (id: string) =>
    request<OperationWeeklyPlanDto>('/operation/weekly-plans/' + id + '/confirm', { method: 'POST' }),

  operationItemCreate: (
    planId: string,
    body: {
      title: string;
      columnId?: string;
      format?: string;
      plannedDate?: string;
      estimatedPoints?: number;
      /** 微元；**留空 = 未知**，不要用 0 冒充"不知道" */
      estimatedMicro?: string;
    },
  ) => request<OperationPlanItemDto>('/operation/weekly-plans/' + planId + '/items', { method: 'POST', body }),

  /** 改 plannedDate **必须**带 movedReason（没有会被 400 拒），后端会自动记 movedFromDate */
  operationItemUpdate: (id: string, body: Record<string, unknown>) =>
    request<OperationPlanItemDto>('/operation/weekly-plan-items/' + id, { method: 'PATCH', body }),

  operationItemRemove: (id: string) => request<{ ok: true }>('/operation/weekly-plan-items/' + id, { method: 'DELETE' }),

  /**
   * 本周预算口径（A3）。
   *
   * ★ `usedPoints` 在有真实执行成本之前**恒为 null**，并带 `usedUnavailableReason`。
   *   前端**必须**显示「未采集 + 原因」，不许当 0 显示（"未采集 ≠ 0"是硬规则）。
   */
  operationBudget: (id: string, weekStart?: string) =>
    request<OperationBudgetDto>(
      '/operation/accounts/' + id + '/budget' + (weekStart ? '?weekStart=' + encodeURIComponent(weekStart) : ''),
    ),
};

/**
 * 生意档案完整详情
 *
 * ★ 字段必须与后端 `business.service.ts` 的 `serialize()` 一一对应。
 *   曾经 serialize 漏了 onCameraWilling / weeklyCapacity / preferredForms，
 *   导致这三个字段写得进、读不出（表单回填不上）。两边以后要一起改。
 */
export interface BusinessDetail {
  id: string;
  publicId: string;
  name: string;
  industry: string | null;
  description: string | null;
  products: string | null;
  priceCents: number | null;
  targetMarket: string | null;
  differentiator: string | null;
  currentChannels: string[];
  onCameraWilling: string | null;
  weeklyCapacity: string | null;
  preferredForms: string[];
  wizardStep: number;
  wizardDone: boolean;
  createdAt: string;
}

/** 定位报告（GET 与 POST 两个接口的返回形状**必须一致**） */
export interface Positioning {
  id: string;
  targetAudience: Array<{ name: string; profile: string; painPoint: string; decisionFactor: string }>;
  sellingPoints: Array<{ point: string; reason: string }>;
  persona: { name: string; style: string };
  contentPillars: string[];
  cadence: string;
  summary?: string;
  createdAt?: string;
  /**
   * ★ 两个接口都得给 meta。
   *
   * 曾经 GET 版本没有 meta，而模板读 report.meta.modelId ——
   * 结果"重新打开已生成过报告的档案"时组件渲染直接抛异常、**整页白屏**。
   * 前端这里全部用 `meta?.` 访问，作为第二道防线。
   */
  meta?: {
    /**
     * 定位报告的模型成本。
     *
     * ★ 注意：这个 costYuan 是**报告自身**的成本口径，与任务计费无关，**要保留**。
     *   我上一版补丁按 `costYuan: string;` 做全局替换时，误删的是**这里**而不是
     *   任务 DTO 里的那一个 —— 被 tsc 抓出来了。改这类同名字段必须带上下文。
     */
    costYuan: string;
    modelId: string;
    provider?: string | null;
    pointsCharged: number;
    free: boolean;
    /** 这条是历史记录（GET）还是刚生成的（POST） */
    fromHistory?: boolean;
    generatedAt?: string;
  };
}

export interface AssetDto {
  id: string;
  kind: string;
  kindLabel: string;
  name: string;
  url: string;
  mimeType: string | null;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  status: string;
  tags: string[];
  referenceCount: number;
  appearancePrompt: string | null;
  /** 是否已登记授权（一致性角色必须为 true 才能用于生成） */
  hasConsent: boolean;
  consentType: string | null;
  consentNote: string | null;
  createdAt: string;
}

export interface SkillDto {
  key: string;
  name: string;
  icon: string;
  description: string;
  utterances: string[];
  category: string;
  contentForm: string | null;
  estimatedPoints: number;
  steps: Array<{ label: string; required: boolean }>;
  /** 还缺哪些前置条件 */
  missing: string[];
  runnable: boolean;
  blockedReason: string | null;
  /**
   * ★ 独立入口：这个工具要先贴一段东西进来（比如爆款文案），
   * 通用确认框装不下，得去它自己的页面。有值时按钮是「打开」而不是「运行」。
   */
  entry?: string | null;
}

/** 文字交付成果（营销方案 / 直播方案 / 跟进 SOP / 数据复盘 / 爆款复刻） */
export interface DeliverableDto {
  publicId: string;
  planKind: string;
  planKindLabel: string;
  title: string;
  /** Markdown 正文（后端由 sections 拼出来，前端一般直接用 sections 渲染） */
  body?: string;
  sections: Array<{ heading: string; bullets: string[] }>;
  evidence: Array<{ text: string; source: string }>;
  unverifiedClaims: ClaimItem[];
  claimSummary: ClaimSummary | null;
  hypotheses: Array<{ metric: string; expectation: string; howToVerify: string }>;
  assumptions: string[];
  /**
   * 平台自检：我们要求的那几块这次覆盖了哪些。
   * ⚠️ 这是"有没有写到"，不是"写得好不好"。
   */
  coverage?: { parts: Array<{ label: string; present: boolean }>; missing: string[] } | null;
  /** 本次扣费（生成接口才有） */
  pointsCharged?: number;
  status?: string;
  createdAt?: string;
}

/** 售后工单 */
export interface SupportTicketDto {
  id: string;
  tenantId: string;
  userId: string;
  category: string;
  title: string;
  body: string;
  status: 'open' | 'replied' | 'closed';
  /**
   * 等谁回复 —— **服务端算出来的**，不是又一个状态字段。
   * 列表页据此标「等你回复 / 等我们回复」；closed 时为 null。
   */
  waitingFor: 'user' | 'platform' | null;
  relatedType: string | null;
  relatedId: string | null;
  lastReplyBy: 'user' | 'platform' | null;
  lastReplyAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** 列表接口不返回消息；详情接口才有 */
  messages?: Array<{
    id: string;
    authorType: 'user' | 'platform';
    authorId: string | null;
    body: string;
    createdAt: string;
  }>;
  /** 附件（与消息并列，不挂在某条消息下）。列表接口不返回 */
  attachments?: SupportAttachmentDto[];
}

/**
 * 工单列表的**窗口口径**（2026-10-04 补）
 *
 * ── 为什么列表接口必须回这三个数 ──
 *
 * 接口原来只回一个数组，而窗口是固定的（默认 50、上限 100）——
 * 第 51 条起在界面上**看不到、也没有任何提示**，而页面上既没有翻页也没有搜索。
 * 前端的渲染方式（数组）看不出来"少东西"，所以只能由服务端如实交代：
 *   · `total`      当前筛选条件下的总条数（不是本次返回的条数，也不是全库）
 *   · `take`       服务端**实际生效**的窗口（请求越界时是夹过的值）
 *   · `truncated`  是否被窗口切掉了尾巴
 * ⚠️ 界面必须把 `total > items.length` 的差额真的算出来说出来，
 *    不许写"大概""若干"这类含糊词。
 */
export interface SupportTicketList {
  items: SupportTicketDto[];
  /** 当前筛选条件下的总条数（与 items 用同一个 where 算出来的） */
  total: number;
  /** 服务端实际生效的窗口大小 */
  take: number;
  /** 还有更早的一页（波次 9 改成键集游标；`truncated` 这个名字已经退役） */
  hasMore: boolean;
  /** 不透明的翻页游标；null = 到底了 */
  nextCursor: string | null;
  /** 是否带了筛选（界面据此区分"筛选结果"与"全部"） */
  filtered: boolean;
}

/**
 * 工单附件
 *
 * ⚠️ **没有 url 字段**，这是有意的：附件一律走带鉴权的下载接口
 * （`GET /support/attachments/:id`）。本地驱动的 `/media` 是匿名直出的，
 * 客户的后台截图不该放在"知道链接就能看"的地方。
 * 所以前端要预览/下载，都得用 `api.ticketAttachmentBlob(id)` 拉回来再用。
 */
export interface SupportAttachmentDto {
  id: string;
  authorType: 'user' | 'platform';
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  /** 是图片才画缩略图；其它类型只给"下载" */
  isImage: boolean;
  createdAt: string;
}

export interface WorkDto {
  id: string;
  title: string;  contentForm: string | null;
  costTier: string;
  status: string;
  durationSec: number | null;
  ratio: string | null;
  /**
   * 成片分辨率（像素）。2026-10-04 之前的作品**没有记录** → `null`，
   * 界面显示"—"，**不要**按画幅推算一个"看起来确定"的值填上。
   */
  width: number | null;
  height: number | null;
  /** 服务端拼好的展示串（`720x1280`）；老作品为 null。前端不必各写一次拼接 */
  resolution: string | null;
  coverUrl: string | null;
  videoUrl: string | null;
  sizeBytes: number | null;
  aiLabelStatus: string;
  exportable: boolean;
  effect: { views: number | null; likes: number | null; conversions: number | null; note: string | null };
  exportCount: number;
  createdAt: string;
}

/**
 * 作品库的**分页口径**（2026-10-04 波次 8：键集游标）
 *
 * ⚠️ `GET /works` 以前返回**裸数组**并静默取前 30 条；再往前一版是"提档到 100 就停"
 *    （作品库没有日期筛选 ⇒ 第 100 条之后**永远够不到**）。
 *    现在用 (createdAt,id) 复合游标翻页：不漏不重，`nextCursor` 是**不透明**字符串。
 */
export interface WorkPage {
  items: WorkDto[];
  total: number;
  take: number;
  hasMore: boolean;
  nextCursor: string | null;
  filtered: boolean;
}

/** 支付通道状态（`GET /billing/pay-channels`，无需登录） */
export interface PayChannelInfo {
  kind: 'wechat' | 'alipay' | 'mock';
  /** 能不能真的收钱。false 时前端**不许**说"去支付" */
  productionReady: boolean;
  reason?: string;
}

export interface PayChannelsDto {
  /**
   * 测试环境标记（由**服务端**判定）。
   *
   * 前端据此决定要不要给"模拟支付"这种测试入口 —— 自己在线上猜是错的：
   * 生产上如果只配了 mock 适配器，channels 里也会出现 mock，
   * 而 `mock-pay` 在生产是硬拒绝的，于是会做出一个点了必错的假按钮。
   */
  sandbox: boolean;
  channels: PayChannelInfo[];
  /**
   * 给**用户**看的"为什么不能充值"。
   *
   * ⚠️ 不要用 `channels[].reason` 顶替它：那是适配器的内部说明
   *    （生产实测显示「Mock 支付通道会给任何人发积分，生产环境强制禁用」），
   *    用户看了只会更困惑，而且没告诉他现在该怎么办。
   *    有可用通道时为 null。
   */
  unavailableReason: string | null;
}

export interface RechargeOrderCreated {
  orderNo: string;
  packageName: string;
  amountCents: number;
  amountYuan: string;
  points: number;
  bonusPoints: number;
  totalPoints: number;
  channel: string;
  /** 渠道返回的支付参数。**形状由渠道决定**，没有真渠道时是测试数据 */
  payParams: unknown;
  expiredAt: string;
  /** false 时前端必须显著提示"这不是真实收款通道" */
  productionReady: boolean;
  note: string | null;
}

export interface RechargeOrderDto {
  orderNo: string;
  amountCents: number;
  amountYuan: string;
  points: number;
  bonusPoints: number;
  totalPoints: number;
  status: string;
  statusText?: string;
  channel: string | null;
  paidAt: string | null;
  createdAt: string;
  expiredAt: string | null;
}

export interface WorkDetailDto extends WorkDto {
  hook: string | null;  shots: Array<{ no: number; from: number; to: number; visual: string; voice: string; imagePrompt?: string }>;
  labels: Array<{
    status: string;
    mediaType: string;
    explicitWatermark: boolean;
    explicitAtStartSec: number | null;
    explicitStyle: string | null;
    implicitMetadata: boolean;
    providerCode: string | null;
    contentNo: string | null;
    watermarkEmbedded: boolean;
    outputHash: string | null;
    errorMsg: string | null;
    createdAt: string;
  }>;
  exports: Array<{
    platform: string;
    ratio: string | null;
    status: string;
    labelStatus: string;
    blockedCode: string | null;
    fileUrl: string | null;
    errorMsg: string | null;
    createdAt: string;
  }>;
}

export interface AgentPlanItem {
  key: string;
  label: string;
  detail: string;
  /** 本次实际计入报价的积分（不可执行的步骤为 0） */
  points: number;
  /** 该步骤完整实现后的预估积分，让用户知道成本结构 */
  estimatedPoints: number;
  available: boolean;
  unavailableReason?: string;
}

export interface AgentStepResult {
  key: string;
  ok: boolean;
  message: string;
  data?: unknown;
}

export interface AgentPlanDto {
  planId: string;
  /**
   * 所属会话（BUG-07）
   *
   * 有它，点侧栏历史计划就能**直接打开那个会话**；
   * 为 null 表示这是会话功能上线前的"孤儿计划"，前端单独说明。
   */
  conversationId?: string | null;
  requestText: string;
  title: string;
  summary: string;
  items: AgentPlanItem[];
  totalPoints: number;
  costTier: string;
  contentForm: string | null;
  missingInfo: string[];
  /**
   * draft=待确认 / confirmed=已确认执行 / expired=报价过期
   * / canceled=用户主动取消 / **superseded=被修订版取代**（BUG-04 B1）
   *
   * superseded 与 canceled 必须分开：用户并没有取消，是我们把它换掉了。
   * 界面显示"已取消"就是在说假话，用户会以为自己点错了什么。
   */
  status: 'draft' | 'confirmed' | 'expired' | 'canceled' | 'superseded';
  /** BUG-04：被哪条修订版取代（null = 这还是当前版本） */
  supersededBy?: string | null;
  /** BUG-04：这条本身是修订版 */
  isRevision?: boolean;
  /** BUG-04：修订号，从 1 开始 */
  revision?: number;
  /** BUG-04：修订自哪条计划 */
  revisionOf?: string | null;
  /** BUG-04（B1）：任务分类与结构化约束 */
  planKind?: string | null;
  planKindLabel?: string | null;
  goal?: string | null;
  deliverableTypes?: string[];
  channels?: string[];
  acceptanceCriteria?: string[];
  clarifications?: { questions?: string[]; answers?: unknown[]; assumptions?: string[] } | null;
  expired: boolean;
  expiresAt: string;
  confirmedAt: string | null;
  /** 确认后的执行结果 */
  result: { results: AgentStepResult[]; collected?: { topicId?: string; scriptId?: string } } | null;
  errorMsg: string | null;
  createdAt: string;
  /** 仅 agentPlan 返回 */
  isStory?: boolean;
  estimatedTotal?: number;
  executable?: boolean;
  ttlMinutes?: number;
  params?: { shotCount: number; durationSec: number; topicCount: number; costTier: string };
  /**
   * BUG-04：用户明确说不要的环节 + 实际生效说明。
   *
   * 必须在卡片上显示出来 —— 用户说了「不要出片」，就得看得见那一步真的没了，
   * 并且有一句话确认"已按你的要求跳过"。
   * 否则用户会怀疑自己说了没用（这正是 BUG-04 的原始投诉）。
   */
  forbidden?: string[];
  forbiddenNotes?: string[];
  forbiddenDroppedSteps?: string[];
  /** BUG-08：报价版本与指纹（前端只用于展示，不参与计算） */
  planVersion?: number;
  quoteVersion?: string | null;
  /**
   * BUG-18：确认卡的交付明细。
   *
   * 报告要求明确「候选数、交付数量、画幅、时长、模型、资产、
   * 收费步骤及不可用步骤」—— 用户就是拿这些做决定的。
   *
   * 服务端从**落库的报价快照**推导，所以「刚生成的卡片」和
   * 「从历史恢复的卡片」显示的完全一致（BUG-06 的教训：两条路径
   * 拼出来的形状不一致，新生成的缺按钮）。
   */
  delivery?: {
    topicCandidates: number;
    images: number;
    videos: number;
    shots: number;
    durationSec: number;
    ratio: string;
    mode: string;
    costTier: string;
    providers: { llm: string[]; image: string[]; video: string[] } | null;
    /** BUG-18：可用通道 ≠ 实际会用的通道，这里说清能不能说准 */
    providerNote?: string;
    chargedSteps: Array<{ key: string; label: string; points: number }>;
    unavailableSteps: Array<{ key: string; label: string; reason: string }>;
    assetNote: string;
  } | null;
}

/** 会话摘要（列表用） */
export interface ConversationSummary {
  id: string;
  title: string;
  status: string;
  messageCount: number;
  businessId: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  /** 末条消息前 60 字 */
  preview: string;
}

/** 会话里的一条消息 */
export interface ConversationMessageDto {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  /** 这条助手消息对应的计划（前端据此在消息流里**原位**渲染报价卡） */
  planId: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

/** 打开会话的返回：消息 + 计划（计划一律以服务端状态为准） */
export interface ConversationDetail {
  conversation: {
    id: string;
    title: string;
    status: string;
    messageCount: number;
    businessId: string | null;
    createdAt: string;
    lastMessageAt: string | null;
  };
  messages: ConversationMessageDto[];
  plans: AgentPlanDto[];
}

export interface TaskStageItem {
  key: string;
  label: string;
  state: 'todo' | 'current' | 'done' | 'stopped';
}

/**
 * 生成中心任务列表的**分页口径**（2026-10-04 波次 8：键集游标）
 *
 * `total` 是**当前筛选下**的总数；`hasMore` 表示还有更早的一页；
 * `nextCursor` 是**不透明**的翻页游标（原样回传，别去解析它）。
 * 上一版是"把 take 提到 100 就停" —— 第 100 条之后永远够不到，现在用游标翻。
 */
export interface TaskPage {
  items: GenerationTaskDto[];
  total: number;
  take: number;
  hasMore: boolean;
  nextCursor: string | null;
  filtered: boolean;
}

export interface GenerationTaskDto {
  id: string;
  taskType: string;
  typeText: string;
  status: string;
  statusText: string;
  stage: string | null;
  stageText: string | null;
  progress: number;
  stages: TaskStageItem[];
  contentForm: string | null;
  costTier: string;
  title: string;
  pointsCharged: number | null;
  /**
   * BUG-16：这里原来有 costYuan（我们的真实模型成本）。
   * 它已从**用户可达的接口**里移除 —— 只藏 UI 不够，
   * 用户开开发者工具照样能从响应体读到。完整明细见管理端
   * GET /admin/tasks/:id/diagnostics。
   */
  errorCode: string | null;
  errorMsg: string | null;
  /**
   * BUG-15：费用状态**来自账本**，不是前端猜的。
   *
   * 改之前前端把「积分已退回」硬编码在错误文案里，而核账本发现
   * **从来没有发生过一笔真正的退款** —— 失败任务走的是 hold→release（冻结释放），
   * 那笔钱从未离开过用户账户。说"已退回"是错的。
   * 三种口径必须分开：已退回 / 未扣费 / 冻结已释放。
   */
  fee?: {
    state: 'none' | 'held' | 'charged' | 'released' | 'refunded' | 'partial';
    text: string;
    chargedPoints: number;
    heldPoints: number;
    releasedPoints: number;
    refundedPoints: number;
  };
  /** 失败/取消时额外给一句"你的钱怎么样了" */
  feeNote?: string | null;
  /**
   * 任务的产出结果。**异步链路下前端靠它拿成品** ——
   * 轮询到 succeeded 时，成片的 URL / 尺寸 / AI 标识都在这里
   * （同步模式下由 HTTP 响应直接返回，这个字段用不上）。
   */
  output?: {
    workId?: string;
    videoUrl?: string;
    coverUrl?: string | null;
    width?: number;
    height?: number;
    ratio?: string;
    durationSec?: number;
    shotCount?: number;
    mode?: string;
    sizeBytes?: number;
    aiLabel?: { status: string; exportable: boolean; note: string };
  } | null;
  retryable: boolean;
  attemptCount: number;
  maxAttempts: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  elapsedSec: number;
}

export interface GenerationTaskDetailDto extends GenerationTaskDto {
  inputParams: unknown;
  outputResult: unknown;
  modelCalls: Array<{
    provider: string;
    capability: string;
    /**
     * BUG-16：token 数、模型 ID 与真实成本**已从用户接口移除**，
     * 只在管理端 `GET /admin/tasks/:id/diagnostics` 可见。
     * 只藏 UI 是不够的 —— 用户开开发者工具照样能从响应体读到。
     */
    durationMs: number;
    success: boolean;
    createdAt: string;
  }>;
}

// ============================================================
//  对标相关类型
// ============================================================

export interface BenchmarkMetrics {
  likeMedian: number | null;
  likeAvg: number | null;
  collectLikeRatio: number | null;
  shareLikeRatio: number | null;
  hitThreshold: number | null;
}

export interface BenchmarkCadence {
  firstDate: string | null;
  lastDate: string | null;
  byMonth: Record<string, number>;
  avgPerWeek: number;
}

export interface BenchmarkWorkItem {
  id: string;
  desc: string | null;
  cover: string | null;
  durationMs: number | null;
  likes: number;
  comments: number;
  shares: number;
  collects: number;
  publishedAt: string | null;
  isHit: boolean;
}

export interface BenchmarkAnalyzeResult {
  accountId: string;
  platformUid: string;
  nickname: string | null;
  uniqueId: string | null;
  avatar: string | null;
  signature: string | null;
  followers: number | null;
  totalFavorited: number | null;
  awemeCount: number | null;
  dataSource: string;
  sourceLabel: string;
  /** ★ 数据源是否已获商用授权 —— 未获授权时前端必须显著提示 */
  commerciallyLicensed: boolean;
  licenseNote?: string;
  /** 数据完整性声明，避免把「拿不到」显示成「没有」 */
  caveats: string[];
  metrics: BenchmarkMetrics;
  cadence: BenchmarkCadence;
  worksCount: number;
  works: BenchmarkWorkItem[];
}

export interface BenchmarkStoredInsight {
  accountSummary: string;
  contentPillars: string[];
  opportunities: Array<{ label: string; tone: 'success' | 'warning' | 'danger' }>;
  explainedCount: number;
  pointsCharged: number;
  modelId: string;
}

export interface BenchmarkAccountDetail {
  id: string;
  platform: string;
  platformUid: string;
  nickname: string | null;
  uniqueId: string | null;
  avatar: string | null;
  signature: string | null;
  followers: number | null;
  totalFavorited: number | null;
  awemeCount: number | null;
  dataSource: string | null;
  /** BUG-10: 授权依据 —— 这份样本凭什么可以拿来用 */
  licenseBasis?: string | null;
  /** BUG-10: 平台展示名（抖音 / 小红书 / 快手 / 视频号 / 未知平台） */
  platformLabel?: string;
  fetchedAt: string | null;
  /** 账号级 AI 拆解结论（未拆解过为 null） */
  insight: BenchmarkStoredInsight | null;
  insightAt: string | null;
  metrics: BenchmarkMetrics;
  works: Array<
    BenchmarkWorkItem & {
      platformWorkId: string;
      hitRatio: number | null;
      transferableFormula: string | null;
      insightConfidence: string | null;
      caution: string | null;
    }
  >;
}
